import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { formatApiErrorDetail } from "@/lib/api";
import { PortalBrand } from "@/components/portal/PortalLayout";
import { Field } from "@/pages/portal/PortalLogin";
import { toast } from "sonner";

export default function PortalResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      await portalApi.post("/portal/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/portal/login"), 2500);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F1222] p-6" data-testid="portal-reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><PortalBrand light /></div>
        {!token ? (
          <div className="bg-[#171B2E] border border-white/10 rounded-2xl p-8 text-center" data-testid="portal-reset-no-token">
            <Icons.AlertTriangle className="h-10 w-10 text-[#F5A623] mx-auto mb-3" />
            <p className="text-slate-300 text-sm">This reset link is invalid. Please request a new one from the sign-in page.</p>
            <button onClick={() => navigate("/portal/login")} className="mt-5 text-[#F5A623] text-sm hover:underline">Go to sign in</button>
          </div>
        ) : done ? (
          <div className="bg-[#171B2E] border border-white/10 rounded-2xl p-8 text-center" data-testid="portal-reset-done">
            <Icons.CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
            <h1 className="font-heading text-xl font-bold text-white">Password reset</h1>
            <p className="text-slate-400 text-sm mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <div className="bg-[#171B2E] border border-white/10 rounded-2xl p-8">
            <h1 className="font-heading text-2xl font-bold text-white">Set a new password</h1>
            <p className="text-slate-400 text-sm mt-1 mb-6">Choose a strong password for your secure client portal.</p>
            <form onSubmit={submit} className="space-y-4" data-testid="portal-reset-form">
              <Field icon="Lock" type="password" value={password} onChange={setPassword} placeholder="New password (min 8 chars)" testid="portal-reset-password" />
              <Field icon="Lock" type="password" value={confirm} onChange={setConfirm} placeholder="Confirm new password" testid="portal-reset-confirm" />
              <button type="submit" disabled={busy} data-testid="portal-reset-submit"
                className="w-full bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-60 text-[#171B2E] font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2">
                {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.ShieldCheck className="h-4 w-4" />} Reset Password
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
